/**
 * v3 — Subtle confident
 * The current send-ready version. No agenda slide, no "we have no ask"
 * narration. Same content as v2 but lets the deck just act like an intro
 * without explaining that it's one.
 */
import {
  Slide,
  Eyebrow,
  Display,
  Headline,
  Lede,
  Body,
  Tag,
  Em,
  Kicker,
  ConditionStrip,
  StatRow,
  Stat,
  OpportunityGrid,
  OpportunityCard,
  ProductGrid,
  Quote,
  Sources,
  Cite,
  Beliefs,
  Belief,
  ContactRow,
  SlideFoot,
} from '@/shared';
import screenHome from '@/assets/screen-home.png';
import screenEntity from '@/assets/screen-entity.png';
import screenBuy from '@/assets/screen-buy.png';
import screenTrade from '@/assets/screen-trade.png';

export default function getSlides() {
  return [
      // Cover
      <Slide variant="cover">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Tag>For the Legend Story Studios team</Tag>
          <Tag variant="mute">May 2026</Tag>
        </div>
        <div style={{ flex: 1 }} />
        <Display>
          Nanza, and the world of the modern <Em>collector</Em>.
        </Display>
        <Lede>
          A short tour of what we've built, what we've learned, and a few of the things about
          Flesh and Blood that have caught our attention.
        </Lede>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '2rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--color-ink-mute)', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Nanza
          </span>
          <Kicker>nanza.app</Kicker>
        </div>
      </Slide>,

      // Who we are
      <Slide>
        <Eyebrow>Nanza</Eyebrow>
        <Headline>
          Nanza is a mobile-first companion app for trading card collectors.
        </Headline>
        <Lede>
          Discover, value, list, buy, sell, and trade — built for the way collectors actually
          behave on their phones, with native respect for condition, printing, and treatment. We're
          live today across One Piece, Magic, and Pokémon.
        </Lede>
        <ConditionStrip />
        <SlideFoot
          left="Founded 2024 · Mobile iOS & Android · Backed by [investors]"
          right="The condition spectrum, native to every Nanza listing."
        />
      </Slide>,

      // Product
      <Slide>
        <Eyebrow>The product</Eyebrow>
        <Headline>
          A marketplace that <Em>speaks card</Em>.
        </Headline>
        <ProductGrid
          frames={[
            { src: screenHome, alt: 'Nanza home screen with seller listing', caption: 'Discover' },
            { src: screenEntity, alt: 'Card detail with condition and printing', caption: 'Card detail' },
            { src: screenBuy, alt: 'Bulk purchase modal', caption: 'Bulk buy' },
            { src: screenTrade, alt: 'Trade and search interface', caption: 'Trade' },
          ]}
        />
        <Body maxWidth="70ch">
          Native condition grading, printing variants, alt arts, and bulk transactions — first-class.
          Every listing carries the metadata a collector actually evaluates before purchase.
          Discovery is built for browsing the way people actually shop on phones, not for parsing
          a catalog.
        </Body>
      </Slide>,

      // Landscape
      <Slide>
        <Eyebrow>The landscape</Eyebrow>
        <Headline>
          The TCG world is bigger and more mobile than the tools serving it.
        </Headline>
        <StatRow>
          <Stat value={<>$13<Em>B</Em></>} citation={1} label="Estimated global trading cards market in 2024, growing at ~8.5% CAGR through 2034." />
          <Stat value={<>15.3<Em>M</Em></>} citation={2} label="Cards submitted to PSA for grading in 2024 alone — a 16% YoY increase." />
          <Stat value={<>~36<Em>%</Em></>} citation={3} label="Of physical TCG collectors actively trade in secondary markets and at events." />
        </StatRow>
        <Body maxWidth="64ch">
          The category is mature enough to deserve native infrastructure. The collectors are
          mobile, condition-fluent, and increasingly treating cards as both a hobby and a soft
          asset class — and the tools they use don't yet match the seriousness with which they
          engage.
        </Body>
        <Sources
          sources={[
            {
              number: 1,
              text: 'Market Decipher / PRNewswire, January 2025 — global trading cards market valuation.',
              url: 'https://www.prnewswire.com/news-releases/2025---trading-cards-market-size-is-estimated-at-13-billion-in-2024-and-is-expected-to-grow-at-a-cagr-of-8-5--during-the-forecast-period-market-decipher-302361699.html',
            },
            {
              number: 2,
              text: 'Mordor Intelligence, Trading Card Game Market report 2025, citing PSA submission data.',
              url: 'https://www.mordorintelligence.com/industry-reports/trading-card-game-market',
            },
            {
              number: 3,
              text: 'Business Research Insights, Trading Card Game Market report 2026 — physical card segmentation analysis.',
              url: 'https://www.businessresearchinsights.com/market-reports/trading-card-game-market-103129',
            },
          ]}
        />
      </Slide>,

      // Convictions
      <Slide>
        <Eyebrow>What we believe</Eyebrow>
        <Headline>Four things we built the product around.</Headline>
        <OpportunityGrid columns={2}>
          <OpportunityCard eyebrow="Conviction 01" title="The phone is the catalog.">
            Discovery, photography, listing, chat — collectors do all of it on mobile. Most
            marketplaces still feel like desktop catalogs that have been responsively shrunk down.
            We started from the phone.
          </OpportunityCard>
          <OpportunityCard eyebrow="Conviction 02" title="Condition is non-negotiable.">
            The difference between Near Mint and Very Good is the entire game in this category.
            Surfaces that flatten condition into "Used" lose serious collectors on the first
            transaction.
          </OpportunityCard>
          <OpportunityCard eyebrow="Conviction 03" title="Heroes, not SKUs.">
            People don't shop by set code, they shop by character. By artist. By treatment. The
            catalog should reflect how collectors actually think — narrative-first, not
            database-first.
          </OpportunityCard>
          <OpportunityCard eyebrow="Conviction 04" title="The local store is part of the system.">
            Online and in-person aren't competitors — they're two halves of the same hobby. The
            products that ignore the LGS layer eventually find out what they're missing.
          </OpportunityCard>
        </OpportunityGrid>
      </Slide>,

      // Pull quote
      <Slide variant="quote">
        <Quote attribution="— James White, founder & CEO, Legend Story Studios" citation={4}>
          To me, the true beauty of trading card games is actually that in-person interaction.
        </Quote>
        <Sources
          sources={[
            {
              number: 4,
              text: 'NZ Herald, "Inside the high-profit business of NZ\'s card game pioneer," July 2025.',
              url: 'https://www.nzherald.co.nz/business/markets-with-madison/legend-story-studios-flesh-and-blood-inside-the-high-profit-business-of-nzs-card-game-pioneer/QOXTWFDJWVBN5FAN5EBDYY264I/',
            },
          ]}
        />
      </Slide>,

      // FaB observations
      <Slide>
        <Eyebrow>Flesh and Blood</Eyebrow>
        <Headline>
          A few things about FaB that have <Em>caught our attention</Em>.
        </Headline>
        <OpportunityGrid columns={2}>
          <OpportunityCard eyebrow="Observation 01" title="The community is unusually serious.">
            2,000+ stores running weekly Armory events,<Cite n={5} /> a $2M World Tour prize pool
            in 2026,<Cite n={6} /> and a brand storytelling award most game studios don't even
            compete for.<Cite n={7} /> This doesn't look like a typical TCG audience.
          </OpportunityCard>
          <OpportunityCard eyebrow="Observation 02" title="The IRL-first stance is a feature, not a constraint.">
            The "in the flesh and blood" mission and the no-digital-play position give the brand a
            coherence most competitors lack. Anything that touches FaB has to take that seriously,
            not work around it.
          </OpportunityCard>
          <OpportunityCard eyebrow="Observation 03" title="The collector layer is sophisticated.">
            Cold Foils, Rainbow Foils, extended art, Marvels, and Living Legend rotations create a
            treatment-and-rarity system that rewards exactly the kind of catalog precision Nanza
            was built for.
          </OpportunityCard>
          <OpportunityCard eyebrow="Observation 04" title="Existing partnerships set a clear pattern.">
            The TCGplayer collaborations are interesting — every campaign is framed through value
            to local game stores, with hundreds of thousands of dollars routed back to LGS
            partners.<Cite n={8} /> That's the model.
          </OpportunityCard>
        </OpportunityGrid>
        <Sources
          sources={[
            { number: 5, text: 'Legend Story Studios, About Us — 2,000+ LGS partners.', url: 'https://legendstory.com/about-us/' },
            { number: 6, text: 'Wikipedia, Flesh and Blood (card game), citing official LSS organized play data — 2026 World Tour prize pool.', url: 'https://en.wikipedia.org/wiki/Flesh_and_Blood_(card_game)' },
            { number: 7, text: 'Legend Story Studios, "Excellence in Brand Storytelling," 2023 New Zealand International Business Awards.', url: 'https://legendstory.com/news/' },
            { number: 8, text: 'TCGplayer Press, multiple D2C campaigns 2021–2024 — $175,000+ to LGS partners.', url: 'https://seller.tcgplayer.com/press-center/tcgplayer-and-legend-story-studios-announce-new-direct-to-consumer-campaign-designed-to-benefit-local-hobby-stores' },
          ]}
        />
      </Slide>,

      // Open threads
      <Slide>
        <Eyebrow>What we're paying attention to</Eyebrow>
        <Headline>
          Open threads in our <Em>thinking</Em>.
        </Headline>
        <Beliefs>
          <Belief label="Thread 01">
            The role of the secondary market as a feedback signal — for publishers, for stores,
            and for collectors trying to read what a community values.
          </Belief>
          <Belief label="Thread 02">
            Where digital companion experiences strengthen the IRL game, and where they start
            pulling players away from the table.
          </Belief>
          <Belief label="Thread 03" muted>
            How online infrastructure routes real value back to local game stores rather than
            around them, and what's worked in the partnerships we've studied.
          </Belief>
          <Belief label="Thread 04" muted>
            Collector-driven sets, alt arts, and rotation-style mechanics — the information layer
            collectors deserve as treatment systems get more sophisticated.
          </Belief>
        </Beliefs>
      </Slide>,

      // Closing
      <Slide variant="closing">
        <Eyebrow>Nanza</Eyebrow>
        <Display>
          Thanks for the <Em>time</Em>.
        </Display>
        <Lede>
          Always happy to talk cards, collectors, or what we're seeing across the category.
        </Lede>
        <ContactRow
          items={[
            { label: 'Founder', value: <>[Name]<br />founder@nanza.app</> },
            { label: 'The team', value: <>[Names]<br />hello@nanza.app</> },
            { label: 'Online', value: 'nanza.app' },
          ]}
        />
      </Slide>,
    ];
}
